import React from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import { useSelector } from "react-redux";

import "./privacypolicy.css";
import SEO from "../../components/seo/SEO";

const PrivacyPolicy = () => {
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  return (
    <>
      <SEO
        title="Privacy Policy | No Wasted Days"
        description="Privacy Policy for the No Wasted Days application, detailing data collection, usage, and protection."
        canonicalUrl={"https://nowasteddays.io/privacy-policy"}
      />

      {userInfo ? <Header variant="light" /> : <Header variant="light" />}

      <main className="privacy-policy-container">
        <div className="privacy-policy-tile">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: 17th August 2025</p>
          <p>
            Thank you for using NoWastedDays.io, a digital diary web app ("the
            App"). Your privacy is hugely important to us. This Privacy Policy
            explains the types of information the app collects, how we use and
            protect that information, what fields are encrypted, and your
            choices regarding your information. By using the App, you consent to
            the collection and use of your information as described in this
            Privacy Policy.
          </p>

          <section>
            <h2>1. Information We Collect</h2>

            <h3>1.1 Personal Information</h3>
            <p>We may collect the following personal information from you:</p>
            <ul>
              <li>
                <strong>Name:</strong> We collect your name to personalize your
                experience within the App.
              </li>
              <li>
                <strong>Email Address:</strong> We collect your email address to
                create and secure your account and to communicate with you
                regarding important updates or account-related information.
              </li>
              <li>
                <strong>Password:</strong> We securely store an encrypted
                version of your password to protect your account.
              </li>
            </ul>

            <h3>1.2 Journal Page Data</h3>
            <p>
              The App securely captures and encrypts the following data you
              create on the Journal page:
            </p>
            <ul>
              <li>
                <strong>Thank You Statements:</strong> To help you focus on
                positivity and gratitude. These are encrypted.
              </li>
              <li>
                <strong>Detachment Statements:</strong> To allow you to
                healthily separate from the idea of self. These are encrypted.
              </li>
              <li>
                <strong>Assurance Statements:</strong> To help increase
                gratitude for your mind and body. These are encrypted.
              </li>
              <li>
                <strong>Planned Moments:</strong> The accumulated
                actions/thoughts you intend to have in a day. These are
                encrypted.
              </li>
              <li>
                <strong>Remember Today Log:</strong> To help with processing
                events and spark future reflections. These are encrypted.
              </li>
              <li>
                <strong>Leave Behind Statement:</strong> To help disconnect from
                unhealthy thoughts or feelings. These are encrypted.
              </li>
              <li>
                <strong>Moment Scores, Start Score, and End Score:</strong>{" "}
                These scores are collected to track well-being. They are not
                encrypted to allow for efficient processing into insights for
                you.
              </li>
            </ul>

            <h3>1.3 Week Planner Data</h3>
            <p>
              The App collects the following data for the Week Planner to help
              you organize your week:
            </p>
            <ul>
              <li>
                <strong>Weekly Objectives:</strong> High-level goals you set for
                the week. This data is encrypted.
              </li>
              <li>
                <strong>Key Tasks & Steps:</strong> Specific events or tasks you
                log in support of the objectives. This data is encrypted.
              </li>
            </ul>

            <h3>1.4 Year Map Data</h3>
            <p>
              The App collects the following data for the Year Map to facilitate
              long-term planning:
            </p>
            <ul>
              <li>
                <strong>Vision:</strong> Major objectives you wish to achieve
                during the year. This data is encrypted.
              </li>
              <li>
                <strong>Values:</strong> The characteristics you want to embody
                as an individual. This data is encrypted.
              </li>
              <li>
                <strong>Ikigai:</strong> Your purpose or reason for being. This
                data is encrypted.
              </li>
              <li>
                <strong>Quote:</strong> Your favourite quote. This data is
                encrypted.
              </li>
              <li>
                <strong>Date of Birth:</strong> The day on which you were born.
                This data is encrypted.
              </li>
              <li>
                <strong>Name:</strong> Your name. This data is encrypted.
              </li>
            </ul>
          </section>

          <section>
            <h2>2. How We Use Your Information</h2>
            <p>We use your information for the following purposes:</p>
            <ul>
              <li>
                To provide and personalize the services and features of the App.
              </li>
              <li>
                To communicate with you and send important updates or
                notifications.
              </li>
              <li>
                To analyze usage trends and improve the App's functionality and
                user experience.
              </li>
              <li>
                To protect the security and integrity of the App and our users'
                information.
              </li>
              <li>To comply with legal obligations or enforce our rights.</li>
            </ul>
          </section>

          <section>
            <h2>3. Data Retention</h2>
            <p>
              We will retain your personal information for as long as necessary
              to fulfill the purposes outlined in this Privacy Policy, unless a
              longer retention period is required or permitted by law.
            </p>
          </section>

          <section>
            <h2>4. Third-Party Services</h2>
            <p>
              We may use third-party services like Google Analytics that
              collect, monitor, and analyze information to improve the App's
              functionality and user experience. We will never sell or share
              your data. These third-party service providers have their own
              privacy policies that govern the use of your information.
            </p>
          </section>

          <section>
            <h2>5. Children's Privacy</h2>
            <p>
              The App is not intended for children under the age of 13. We do
              not knowingly collect personal information from children under 13.
              If we learn that we have collected personal information from a
              child under 13, we will promptly delete that information.
            </p>
          </section>

          <section>
            <h2>6. Your Choices</h2>
            <p>
              You have the right to access, correct, or delete your personal
              information. You can update or delete your account information
              within the App's settings by visiting your account page.
            </p>
          </section>

          <section>
            <h2>7. Changes to this Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated
              version will be indicated by an updated "Last Updated" date. We
              encourage you to review this Privacy Policy periodically for any
              changes.
            </p>
          </section>

          <section>
            <h2>8. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or
              our privacy practices, please contact us at:
            </p>
            <p className="contact-info">
              NoWastedDays.io
              <br />
              Email: hello@nowastedays.org
            </p>
          </section>
        </div>
      </main>

      <TestFooter />
    </>
  );
};

export default PrivacyPolicy;
